import { Button, Modal, ModalContent, ModalTitle, NativeSelect } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useAccess } from "~/core/access";
import { ApiError, post, request } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import type { ContentItem } from "./api";
import { ContentSnapshot, StudioEditor } from "./studio";
import "./studio.css";

type Block = {
	id: string;
	name: string;
	type: string;
	enabled?: boolean;
	subBlocks: Record<string, { value: unknown }>;
};
export interface ContentFlowData {
	content: boolean;
	artifacts?: Pick<ContentItem, "id" | "title" | "revision" | "document" | "files">[];
	retry?: { allowed: boolean; reason?: string } | null;
	history?: { id: string; created_at: string; success: boolean | null }[];
	deliveries?: {
		id: string;
		item_id: string;
		platform: string;
		source_revision: number;
		current_revision: number;
		task_id: string | null;
		state: string | null;
		published_url: string | null;
	}[];
	name: string;
	document: {
		blocks: Record<string, Block>;
		edges: { source: string; target: string }[];
	};
	run: null | {
		id: string;
		execution_id?: string;
		created_at: string;
		cancelled: boolean;
		error: string | null;
		result: { success?: boolean; error?: { message_key?: string } } | null;
	};
	steps: {
		block_id: string;
		iteration: number;
		output: { result?: { id?: string; revision?: number; resource?: { type?: string; id?: string } } };
		error: { message_key?: string } | null;
	}[];
	waiting: string[];
	operations: { block_id: string; job_id?: string; held_credits?: number; status: string; credits: number | null }[];
}
export function flowFinished(data: ContentFlowData | undefined): boolean {
	return !!(data?.run?.result || data?.run?.error || data?.run?.cancelled);
}
export function flowPollInterval(data: ContentFlowData | undefined, pendingRun = ""): false | number {
	return data?.content === false || (!pendingRun && flowFinished(data)) ? false : 3000;
}
export function orderedBlocks(data: ContentFlowData): Block[] {
	const blocks = Object.values(data.document.blocks).filter((b) => b.type !== "note");
	const left = new Set(blocks.map((b) => b.id));
	const ordered: Block[] = [];
	while (left.size) {
		const next = blocks.find(
			(b) => left.has(b.id) && !data.document.edges.some((e) => e.target === b.id && left.has(e.source)),
		);
		if (!next) return [...ordered, ...blocks.filter((b) => left.has(b.id))];
		ordered.push(next);
		left.delete(next.id);
	}
	return ordered;
}
export function flowItemIds(data: ContentFlowData): string[] {
	return [
		...new Set(
			data.steps.flatMap((step) => {
				const output = step.output?.result;
				const cap = data.document.blocks[step.block_id]?.subBlocks.capability?.value;
				if (typeof cap !== "string" || !cap.startsWith("studio.")) return [];
				const id = output?.resource?.type === "content" ? output.resource.id : output?.id;
				return typeof id === "string" && id.startsWith("ci_") ? [id] : [];
			}),
		),
	];
}
type FlowContext = {
	brandId: string;
	workflowId: string;
	data: ContentFlowData | undefined;
	selectedRun: string;
	selectRun: (id: string) => void;
	refresh: () => void;
	pendingRun: string;
	queueRun: (id: string) => void;
	error: Error | null;
	openItem: (id: string) => void;
};
const ContentFlowContext = createContext<FlowContext | null>(null);
export const useContentFlow = () => useContext(ContentFlowContext);

export function ContentWorkflow(props: { brandId: string; workflowId: string; children: ReactNode }) {
	return <ContentWorkflowProvider key={`${props.brandId}:${props.workflowId}`} {...props} />;
}
function ContentWorkflowProvider({
	brandId,
	workflowId,
	children,
}: {
	brandId: string;
	workflowId: string;
	children: ReactNode;
}) {
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const [search] = useSearchParams();
	const [selectedRun, selectRun] = useState(search.get("run") ?? "");
	const [pendingRun, queueRun] = useState("");
	const [itemId, openItem] = useState("");
	const [dirty, setDirty] = useState(false);
	const query = useQuery({
		queryKey: ["content-flow", brandId, workflowId, selectedRun],
		queryFn: () =>
			request<ContentFlowData>(
				`/agent/content/${brandId}/workflows/${workflowId}${selectedRun ? `?run=${encodeURIComponent(selectedRun)}` : ""}`,
			),
		// The latest run can change when Copilot or a schedule starts another execution.
		refetchInterval: (q) =>
			q.state.data?.content === false ? false : selectedRun ? flowPollInterval(q.state.data, pendingRun) : 3000,
	});
	useEffect(() => {
		if (pendingRun && query.data?.history?.some((run) => run.id === pendingRun)) {
			selectRun(pendingRun);
			queueRun("");
		}
	}, [pendingRun, query.data?.history]);

	return (
		<ContentFlowContext.Provider
			value={{
				brandId,
				workflowId,
				data: query.data,
				pendingRun,
				queueRun,
				selectedRun,
				selectRun,
				refresh: () => {
					void query.refetch();
				},
				error: query.error,
				openItem,
			}}
		>
			{children}
			<Modal
				open={!!itemId}
				onOpenChange={(open) => {
					if (!open && !dirty) openItem("");
				}}
			>
				<ModalContent side="right" aria-describedby={undefined}>
					<div className="detail-panel-head">
						<ModalTitle className="detail-panel-title">{tr("Edit & publish content", "编辑与发布内容")}</ModalTitle>
						<Button variant="ghost" disabled={dirty} onClick={() => openItem("")}>
							{tr("Close", "关闭")}
						</Button>
					</div>
					{dirty && <p className="hint">{tr("Save or discard edits before closing.", "关闭前，请保存或放弃修改。")}</p>}
					<div className="detail-panel-body">
						<p className="hint">
							{tr(
								"Editing the current version. The output saved with this run stays unchanged.",
								"正在编辑当前版本，本次执行保存的产物不会改变。",
							)}
						</p>
						{itemId && (
							<StudioEditor
								key={itemId}
								brandId={brandId}
								itemId={itemId}
								embedded
								inWorkflow
								onDirtyChange={setDirty}
							/>
						)}
					</div>
				</ModalContent>
			</Modal>
		</ContentFlowContext.Provider>
	);
}

export function ContentRunControls() {
	const flow = useContentFlow();
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const t = useT();
	const { editor } = useAccess();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	if (!flow || flow.data?.content === false) return null;
	const { data } = flow;
	const runError = data?.run?.error || data?.run?.result?.error?.message_key;
	return (
		<div className="content-run-controls">
			{(!!data?.history?.length || !!flow.selectedRun) && (
				<NativeSelect
					aria-label={tr("Run history", "执行记录")}
					value={flow.selectedRun}
					onChange={(e) => flow.selectRun(e.target.value)}
				>
					<option value="">{tr("Latest run", "最近一次执行")}</option>
					{!data?.history?.some((run) => run.id === flow.selectedRun) && flow.selectedRun && (
						<option value={flow.selectedRun}>{tr("Selected run", "当前执行记录")}</option>
					)}
					{data?.history?.map((run) => (
						<option key={run.id} value={run.id}>
							{new Date(run.created_at).toLocaleString(locale)}
						</option>
					))}
				</NativeSelect>
			)}
			{data?.retry?.allowed && (
				<Button
					disabled={!editor || busy || !!flow.pendingRun}
					onClick={async () => {
						setBusy(true);
						setError("");
						try {
							const next = await post<{ run_id: string }>(
								`/agent/content/${flow.brandId}/workflows/${flow.workflowId}/retry`,
								{ run_id: data.run?.id },
							);
							flow.queueRun(next.run_id);
							flow.refresh();
						} catch (error) {
							setError(
								error instanceof ApiError
									? error.localized(locale)
									: tr(
											"Could not start continuation. Retry to check the same request.",
											"无法确认续跑是否开始，重试会检查同一次请求。",
										),
							);
						} finally {
							setBusy(false);
						}
					}}
				>
					{tr("Continue failed workflow", "从失败处继续")}
				</Button>
			)}
			{data?.retry && !data.retry.allowed && (
				<span className="hint">
					{tr("Cannot continue: ", "暂不支持续跑：")}
					{t(data.retry.reason ?? "agent_execution_unsupported")}
				</span>
			)}
			{!!data?.waiting.length && (
				<Link to={`/w/${flow.brandId}/tasks`}>{tr("Open pending reviews", "打开待审核任务")}</Link>
			)}
			{runError && <span role="alert">{t(runError)}</span>}
			{flow.error && (
				<span role="alert">
					{tr("Could not refresh execution results.", "无法刷新执行结果。")}{" "}
					<Button onClick={flow.refresh}>{tr("Retry", "重试")}</Button>
				</span>
			)}
			{flow.pendingRun && <span role="status">{tr("Continuation queued…", "续跑已排队…")}</span>}
			{error && <span role="alert">{error}</span>}
		</div>
	);
}

export function contentItemId(output: unknown): string | undefined {
	if (!output || typeof output !== "object") return;
	const result = (output as { result?: { id?: unknown; resource?: { type?: string; id?: unknown } } }).result;
	const id = result?.resource?.type === "content" ? result.resource.id : result?.id;
	return typeof id === "string" && id.startsWith("ci_") ? id : undefined;
}

export function ContentRunOutput({
	executionId,
	blockId,
	output,
	children,
}: {
	children: ReactNode;
	executionId?: string;
	blockId: string;
	output: unknown;
}) {
	const flow = useContentFlow();
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const sameRun = flow?.data?.run && [flow.data.run.id, flow.data.run.execution_id].includes(executionId);
	const query = useQuery({
		queryKey: ["content-flow", flow?.brandId, flow?.workflowId, executionId],
		enabled: !!executionId && !!flow && !sameRun && flow.data?.content !== false,
		queryFn: () =>
			request<ContentFlowData>(
				`/agent/content/${flow!.brandId}/workflows/${flow!.workflowId}?run=${encodeURIComponent(executionId!)}`,
			),
		refetchInterval: (q) => flowPollInterval(q.state.data),
	});
	const data = sameRun ? flow?.data : query.data;
	if (executionId && flow && !sameRun && query.error)
		return (
			<>
				<p role="alert">
					{tr("Could not load the saved output.", "无法加载保存的产物。")}{" "}
					<Button onClick={() => void query.refetch()}>{tr("Retry", "重试")}</Button>
				</p>
				{children}
			</>
		);
	if (!executionId || !flow || !data?.content) return children;
	const capability = data.document.blocks[blockId]?.subBlocks.capability?.value;
	if (typeof capability !== "string" || !capability.startsWith("studio.")) return children;
	const itemId = contentItemId(output);
	const revision = (output as { result?: { revision?: number } } | null)?.result?.revision;
	const versions = data.artifacts?.filter((item) => item.id === itemId) ?? [];
	const artifacts =
		typeof revision === "number" ? versions.filter((item) => item.revision === revision) : versions.slice(-1);
	const operations = data.operations.filter((op) => op.block_id === blockId);
	if (!itemId && !operations.length) return children;
	return (
		<section className="content-run-output">
			{operations.length > 0 && (
				<p className="hint">
					{tr("Credits settled", "积分已结算")} ·{" "}
					{operations.reduce((sum, op) => sum + (op.status === "waiting" ? 0 : (op.credits ?? 0)), 0)}
					{operations.some((op) => op.status === "waiting" || op.credits === null || (op.held_credits ?? 0) > 0)
						? tr(" · Final cost pending", " · 最终费用待结算")
						: ""}
				</p>
			)}
			{artifacts.map((item) => (
				<ContentSnapshot key={`${item.id}:${item.revision}`} item={item} expanded />
			))}
			{itemId && !artifacts.length && (
				<p className="hint">
					{tr(
						"This step returned a saved work. Open it to view its current content.",
						"此步骤返回了已保存的作品，可打开查看当前内容。",
					)}
				</p>
			)}
			{itemId && (
				<Button onClick={() => flow.openItem(itemId)}>
					{tr("Edit & publish current version", "编辑与发布当前版本")}
				</Button>
			)}
			<details>
				<summary>{tr("Raw output", "原始输出")}</summary>
				{children}
			</details>
		</section>
	);
}
